// src/hooks/useAdminUser.js
import { useEffect, useMemo, useState } from "react";
import { useUser } from "../pages/Home/UserContext";
import { getUserAdminPermissions } from "../services/userAdminPermissionServices";

function readStoredUser() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error("Erreur lecture userInfo :", error);
    return null;
  }
}

function normalizeUser(user) {
  if (!user) return null;

  const normalizedId = user?.id || user?.user_id || user?.userId;

  return {
    ...user,
    id: normalizedId,
    userId: normalizedId,
  };
}

export default function useAdminUser() {
  const { user, updateUser } = useUser();
  const [hydratedUser, setHydratedUser] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const baseUser = useMemo(() => {
    return normalizeUser(user || readStoredUser());
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      if (!baseUser) {
        if (!cancelled) {
          setHydratedUser(null);
          setPermissionsLoaded(true);
        }
        return;
      }

      // Admin complet : pas besoin d'aller chercher les permissions
      if (Number(baseUser?.role_id) === 1) {
        if (!cancelled) {
          setHydratedUser(baseUser);
          setPermissionsLoaded(true);
        }
        return;
      }

      // User connecté sans id exploitable
      if (!baseUser?.id) {
        console.warn("useAdminUser: identifiant utilisateur introuvable", baseUser);

        if (!cancelled) {
          setHydratedUser(baseUser);
          setPermissionsLoaded(true);
        }
        return;
      }

      if (!cancelled) {
        setPermissionsLoaded(false);
      }

      try {
        const permissions = await getUserAdminPermissions(baseUser.id);

        const nextUser = {
          ...baseUser,
          admin_permissions: Array.isArray(permissions) ? permissions : [],
        };

        if (cancelled) return;

        setHydratedUser(nextUser);
        setPermissionsLoaded(true);

        try {
          localStorage.setItem("userInfo", JSON.stringify(nextUser));
        } catch (error) {
          console.error("Erreur sauvegarde userInfo :", error);
        }

        if (typeof updateUser === "function") {
          updateUser(nextUser);
        }
      } catch (error) {
        console.error("Erreur hydrate admin user :", error);

        if (!cancelled) {
          setHydratedUser({
            ...baseUser,
            admin_permissions: [],
          });
          setPermissionsLoaded(true);
        }
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [baseUser?.id, baseUser?.role_id]);

  return {
    user: hydratedUser || baseUser || null,
    permissionsLoaded,
  };
}